/**
 * liveAudio.js — one microphone pipeline for BOTH streaming STT and recording.
 *
 * Captures mic audio as 16 kHz mono 16-bit PCM (what Gemini Live expects) and
 * hands out (a) small chunks for streaming, (b) a per-turn WAV for the S3
 * session recording, (c) an RMS level for silence detection.
 *
 * Browser support notes (the cross-browser mandate, 2026-06-28):
 *   • getUserMedia + WebAudio work on Chrome, Safari (incl. iOS 14.1+), Edge,
 *     Firefox. No Web Speech API, no MediaRecorder — both were the sources of
 *     the "works only in Chrome" problem (Natalya's phone couldn't be heard).
 *   • ScriptProcessorNode is deprecated but still universally supported and
 *     needs no separately-served worklet file. Fine at 16 kHz mono for a POC.
 *   • iOS requires a user gesture to unlock audio — call unlockAudio() from
 *     the Tap-to-Start handler.
 */

export const TARGET_RATE = 16000;

// ─── Shared AudioContext (created once, unlocked once) ───
let sharedCtx = null;
export function getAudioCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!sharedCtx || sharedCtx.state === 'closed') sharedCtx = new AC();
  return sharedCtx;
}

// Unlock audio output on iOS/Safari. MUST be called from a user-gesture
// handler (e.g. the Tap-to-Start click). Safe to call repeatedly.
export function unlockAudio() {
  try {
    const ctx = getAudioCtx();
    if (ctx.state === 'suspended') ctx.resume();
    const buf = ctx.createBuffer(1, 1, 22050); // one silent sample
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
  } catch { /* never break the app over audio unlock */ }
}

// ─── Mic capture → 16 kHz Int16 chunks ───
// Returns { stream, stop() }. Calls onChunk(Int16Array) ~every 85ms and
// onLevel(rms) at the same cadence (rms of the RAW input, for VAD).
export async function startCapture({ onChunk, onLevel } = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  const ctx = getAudioCtx();
  if (ctx.state === 'suspended') { try { await ctx.resume(); } catch { /* ignore */ } }

  const source = ctx.createMediaStreamSource(stream);
  const proc = ctx.createScriptProcessor(4096, 1, 1);
  // ScriptProcessor only fires when connected through to the destination;
  // route it through a muted gain so the mic isn't audible back.
  const mute = ctx.createGain();
  mute.gain.value = 0;

  proc.onaudioprocess = (e) => {
    const f32 = e.inputBuffer.getChannelData(0);
    if (onLevel) {
      let sum = 0;
      for (let i = 0; i < f32.length; i += 4) sum += f32[i] * f32[i];
      onLevel(Math.sqrt(sum / (f32.length / 4)));
    }
    if (onChunk) {
      const ds = downsampleTo16(f32, ctx.sampleRate);
      if (ds.length) onChunk(ds);
    }
  };

  source.connect(proc);
  proc.connect(mute);
  mute.connect(ctx.destination);

  let stopped = false;
  return {
    stream,
    stop() {
      if (stopped) return;
      stopped = true;
      try { proc.disconnect(); source.disconnect(); mute.disconnect(); } catch { /* ignore */ }
      stream.getTracks().forEach((t) => t.stop());
    },
  };
}

// Downsample Float32 @ ctx rate → Int16 @ 16 kHz. Window-averaging gives basic
// anti-aliasing; handles non-integer ratios (44.1 kHz on iOS).
function downsampleTo16(f32, fromRate) {
  if (fromRate === TARGET_RATE) return floatTo16(f32);
  const ratio = fromRate / TARGET_RATE;
  const n = Math.floor(f32.length / ratio);
  const out = new Int16Array(n);
  for (let i = 0; i < n; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), f32.length);
    let s = 0;
    for (let j = start; j < end; j++) s += f32[j];
    const v = s / (end - start || 1);
    out[i] = v < 0 ? Math.max(-32768, Math.round(v * 32768)) : Math.min(32767, Math.round(v * 32767));
  }
  return out;
}

export function floatTo16(f32) {
  const out = new Int16Array(f32.length);
  for (let i = 0; i < f32.length; i++) {
    const v = f32[i];
    out[i] = v < 0 ? Math.max(-32768, Math.round(v * 32768)) : Math.min(32767, Math.round(v * 32767));
  }
  return out;
}

// ─── WAV + base64 helpers ───
// Wrap accumulated Int16 chunks into a standard 16 kHz mono PCM WAV.
export function encodeWav(int16Chunks, sampleRate = TARGET_RATE) {
  let len = 0;
  for (const c of int16Chunks) len += c.length;
  const buf = new ArrayBuffer(44 + len * 2);
  const dv = new DataView(buf);
  const wstr = (off, s) => { for (let i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i)); };
  wstr(0, 'RIFF'); dv.setUint32(4, 36 + len * 2, true); wstr(8, 'WAVE');
  wstr(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, sampleRate, true); dv.setUint32(28, sampleRate * 2, true);
  dv.setUint16(32, 2, true); dv.setUint16(34, 16, true);
  wstr(36, 'data'); dv.setUint32(40, len * 2, true);
  let off = 44;
  for (const c of int16Chunks) {
    for (let i = 0; i < c.length; i++) { dv.setInt16(off, c[i], true); off += 2; }
  }
  return new Uint8Array(buf);
}

export function bytesToB64(bytes) {
  let bin = '';
  const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + CH, bytes.length)));
  }
  return btoa(bin);
}

export function int16ToB64(int16) {
  return bytesToB64(new Uint8Array(int16.buffer, int16.byteOffset, int16.length * 2));
}
